/**
 * The settings menu as DATA — sections first, entries inside them.
 *
 * Atle, 1. september: «Del opp i mapper, det kan ikke være 1 lang scroll med innstillinger.»
 * The entries were already one route each; what they were not was GROUPED, so the menu was a flat
 * list that gets longer every time a page lands and tells the reader nothing about which of them
 * answers their question.
 *
 * GROUPED BY WHO ASKS, NOT BY WHICH TABLE IT WRITES TO. «Avdelinger» and «Tags» both write an
 * org-wide list of labels, and they are in different folders, because the person who asks "how is
 * this organisation divided" is not the person who asks "what do we call this kind of contact".
 * Tags and delivery categories ARE the same question — two lists of labels other pages point at —
 * so they share one.
 *
 * WHAT IS NOT HERE. The plan's «Organisasjon: navn, logo», invitations, consents and the document
 * folder template have no page in this application yet, so they have no folder either: an empty
 * section is a promise the menu cannot keep. Adding one later is an entry in the list below and
 * nothing else — `SettingsLayout` reads this file and has no per-entry knowledge of its own.
 *
 * `permission` is the action that OPENS the page — the same one the route's `beforeLoad` asks and
 * the same one the API refuses on. Hiding the entry is UX; the other two are the security
 * (decision 0005: hidden, never disabled).
 */
import type { Action, Actor, OrganizationKind } from '@tvitvi/shared';

import { allows } from '@/auth/actor';
import type { TKey } from '@/i18n/types';

/** Every page the settings menu can point at. The router serves exactly these. */
export type SettingsEntryPath =
  | '/settings/permissions'
  | '/settings/shares'
  | '/settings/ai'
  | '/settings/organization-details'
  | '/settings/departments'
  | '/settings/members'
  | '/settings/tags'
  | '/settings/booking-terms'
  | '/settings/booking-pricing'
  | '/settings/concert-templates'
  | '/settings/contracts';

/** Every folder the settings menu can point at. Each one lands on its first open entry. */
export type SettingsSectionPath = '/settings/organization' | '/settings/access' | '/settings/data';

export type SettingsSectionId = 'organization' | 'access' | 'data';

export interface SettingsEntry {
  /** Test id suffix and React key. Always the last segment of `to`, so the two cannot drift. */
  readonly id: string;
  readonly to: SettingsEntryPath;
  readonly labelKey: TKey;
  /** Absent means everybody who can reach Settings at all can reach this page. */
  readonly permission?: Action;
  /**
   * Hvilke HUS siden finnes i. Utelatt betyr begge.
   *
   * Atle, 9. september: «Innstillinger viser Scenetider, Akkrediteringstyper, Budvilkår og
   * Pris og score» i byråhuset. Et byrå har ingen scener, deler ikke ut akkreditering og
   * setter ingen budvilkår — de fire sidene er festivalens, og en meny som tilbyr dem lyver om
   * hva huset er.
   *
   * Den samme mekanismen som `navigation.ts` bruker for Booking og Festivaler, og med vilje
   * det samme ordet: en leser som lærer at `kinds` betyr «hvilket hus» skal ikke måtte lære
   * det to ganger.
   */
  readonly kinds?: readonly OrganizationKind[];
}

export interface SettingsSection {
  readonly id: SettingsSectionId;
  readonly to: SettingsSectionPath;
  readonly titleKey: TKey;
  readonly entries: readonly SettingsEntry[];
}

/**
 * ORGANISATION ONLY. YOUR OWN ACCOUNT IS NOT IN THIS LIST, AND THAT IS THE POINT.
 *
 * "Profil" used to sit at the top here, which put your name and language in the same menu as
 * rules that govern everybody in the organisation. Atle's call on 28 August: the account belongs
 * behind your own avatar in the header, the organisation belongs in the folder structure on the
 * left. It lives at `/profile`, outside this layout entirely — see `route-tree.ts`.
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    id: 'organization',
    to: '/settings/organization',
    titleKey: 'settings.section.organization',
    entries: [
      /*
       * ØVERST, OG DET ER ATLES ORD (QA12 #9): «Redigere egen organisasjon: øverst i Innstillinger
       * › Organisasjon.» Den som åpner mappa spør først hvem huset ER; avdelinger og scenetider er
       * hvordan det er organisert.
       *
       * `organization:read` er ja for alle fem rollene, og siden viser seg som LESNING for den som
       * ikke har `organization:update`. Å gate den på skrivecella ville skjult organisasjonsnummeret
       * for staben som skal skrive det på en faktura.
       */
      {
        id: 'organization-details',
        to: '/settings/organization-details',
        labelKey: 'settings.nav.organization',
        permission: 'organization:read',
      },
      /*
       * MEDLEMMER (A2, 22. oktober): medlemsoppsettet flyttet hit fra Kontakter — legge til, rolle,
       * tittel, rapporterer til, medlem siden, invitasjoner, deaktivering og «Gi innlogging». Kontakter
       * er én liste over menneskene; hvem som har tilgang og med hvilken rolle, er et oppsett.
       */
      {
        id: 'members',
        to: '/settings/members',
        labelKey: 'settings.nav.members',
        permission: 'membership:list',
      },
      // `department:list` is `yes` for all five roles: everybody sees the groups, because a task
      // carries one. The writes are what narrow, and the page asks can() about those itself.
      { id: 'departments', to: '/settings/departments', labelKey: 'settings.nav.departments' },
      /*
       * SCENETIDER OG AKKREDITERINGSTYPER STO HER TIL 21. SEPTEMBER (QA13 runde 2, #5).
       *
       * Atle: «Begge sidene viser nå «Ingen årgang ennå – Oppsettet hører til en årgang» … Da er
       * det ingen grunn til å gå via org-innstillingene i det hele tatt. Fjern menypunktene og
       * rutene `/settings/stage-times` og `/settings/accreditation-types`. Redigering skjer kun i
       * årgangens Innstillinger.»
       *
       * Og den generelle regelen han skrev i samme punkt: alt som etter K-1 hører til årgangen,
       * skal ikke ha inngang fra org-innstillingene. Den gjelder neste gang noen vurderer å legge
       * en årgangsliste inn her.
       *
       * Rutene er slettet og ikke satt til en videresending, av samme grunn som står i
       * `route-tree.ts`: en adresse som svarer er en adresse noen bokmerker.
       */
      /*
       * Standardvilkår for budgivning (Atle, 4. september). Under «Organisasjon» av samme grunn
       * som de to over: spørsmålet er «hvordan formulerer denne organisasjonen seg når den byr på
       * en artist», ikke «hvilke etiketter finnes». Prinsipp 9 i praksis — teksten om
       * eksklusivitet i Salten hører ikke hjemme i koden.
       */
      {
        id: 'booking-terms',
        to: '/settings/booking-terms',
        labelKey: 'settings.nav.bookingTerms',
        /* Modulen Booking, ikke typen (G1, QA13): «Se Booking» bærer modulen i can(). */
        permission: 'booking:list',
      },
      /*
       * Satsene modellen legger på honoraret, og vektene booking score bruker. Under
       * «Organisasjon» av samme grunn som vilkårene over: spørsmålet er hvordan DENNE
       * organisasjonen vurderer en artist, ikke hvilke etiketter som finnes.
       */
      /*
        BYRÅKOBLINGER ER BORTE (QA12 #10). Kravet om en forhåndskobling er opphevet — bud og
        forespørsler går fram uansett, og ruting skjer på registerbyråets kobling til org-en,
        satt av plattformen. Tabellen står som historikk; innstillingen finnes ikke.
      */
      /*
       * `kinds: ['festival']` ER RIKTIG SIDEN QA14 #3a — OG VAR DET IKKE FØR.
       *
       * Siden bar feltet «Byråets provisjon (%)» ved siden av reise-, opphold- og matsatsene, og
       * gatet er festivalens. Provisjonen var altså synlig for ARRANGØRER og skjult for BYRÅER,
       * som Atle påpekte var baklengs. Feltet er fjernet herfra: satsen per artist settes under
       * Avtale hos byrået, bak `artist_agreement:update`, som siden QA14 bærer `isAgencyHouse` i
       * regellaget.
       *
       * Det som står igjen på siden — satsene og scorevektene — er arrangørens kostnadsestimat og
       * ingenting annet. Gatet svarer derfor nå på det samme spørsmålet som innholdet.
       */
      {
        id: 'booking-pricing',
        to: '/settings/booking-pricing',
        labelKey: 'settings.nav.bookingPricing',
        /* Modulen Booking, ikke typen (G1, QA13). */
        permission: 'booking:list',
      },
      /*
       * KONSERTMALER (agent D, skjerm 12): det en ny konsert skal inneholde fra start. Husets, ikke
       * en årgangs — malen brukes på tvers av år og turneer.
       */
      {
        id: 'concert-templates',
        to: '/settings/concert-templates',
        labelKey: 'settings.nav.concertTemplates',
        permission: 'concert_template:list',
      },
      /* KONTRAKTER (sak 274): fullmaktene og kontraktens tekster. */
      {
        id: 'contracts',
        to: '/settings/contracts',
        labelKey: 'settings.nav.contracts',
        permission: 'contract:read',
      },
    ],
  },
  {
    id: 'access',
    to: '/settings/access',
    titleKey: 'settings.section.access',
    entries: [
      // `role_permission:list` is `yes` for superadmin and `no` for the other four, so this whole
      // folder is absent for four roles out of five — which is the shape a section has to survive.
      {
        id: 'permissions',
        to: '/settings/permissions',
        labelKey: 'settings.nav.permissions',
        permission: 'role_permission:list',
      },
      /* F9: hvem utenfor organisasjonen som har tilgang til hva. Standard superadmin. */
      {
        id: 'shares',
        to: '/settings/shares',
        labelKey: 'settings.nav.shares',
        permission: 'share:list',
      },
      /* B4: AI-bryterne og organisasjonens AI-tilkoblinger. Bare superadmin (den låste cella). */
      {
        id: 'ai',
        to: '/settings/ai',
        labelKey: 'settings.nav.ai',
        permission: 'membership:manage_roles',
      },
    ],
  },
  {
    id: 'data',
    to: '/settings/data',
    titleKey: 'settings.section.data',
    entries: [
      // `tag:list` is `yes` for all five roles too, so this needs no gate either.
      { id: 'tags', to: '/settings/tags', labelKey: 'settings.nav.tags' },
      /*
        LEVERANSEKATEGORIER STO HER TIL 19. SEPTEMBER, OG ER FLYTTET TIL ADVANCEN (#14).

        Atle: «Fjern siden fra org-innstillingene. Når en advance opprettes, får den Promo, Teknisk
        og Hospitality som standardseksjoner. Seksjonene kan gis nytt navn, sorteres, legges til og
        fjernes i den enkelte advancen.»

        Oppføringen er borte framfor skjult: en meny som peker på en side som ikke finnes er en
        lenke til en 404. Gruppene redigeres nå i advancens egen seksjonsvisning.
      */
    ],
  },
];

/** The same question the route guard and the API ask, asked once for the menu. */
export function opensSettingsEntry(actor: Actor | null, entry: SettingsEntry): boolean {
  return entry.permission === undefined || allows(actor, entry.permission);
}

/**
 * The menu this reader gets: entries they cannot open are dropped, and a folder left with nothing
 * in it is dropped with them. An empty heading is worse than a missing one — it advertises a room
 * with no door.
 */
export function settingsSectionsFor(
  actor: Actor | null,
  kind: OrganizationKind = 'festival',
): readonly SettingsSection[] {
  return SETTINGS_SECTIONS.map((section) => ({
    ...section,
    entries: section.entries.filter(
      (entry) =>
        (entry.kinds === undefined || entry.kinds.includes(kind)) &&
        opensSettingsEntry(actor, entry),
    ),
  })).filter((section) => section.entries.length > 0);
}

/**
 * Where `/settings` forwards to.
 *
 * COMPUTED, NOT HARDCODED, and that is the fix. It was `/settings/departments` written into the
 * route, which is right only for as long as `department:list` stays `yes` for everybody — and the
 * matrix is data an owner may edit. The first entry of the first folder is departments today, so
 * nothing moves; the day it is refused, the reader lands on the next thing they can open instead
 * of on a refusal.
 */
export function settingsLandingPath(
  actor: Actor | null,
  kind: OrganizationKind = 'festival',
): SettingsEntryPath {
  const first = settingsSectionsFor(actor, kind).at(0)?.entries.at(0);
  return first?.to ?? '/settings/departments';
}

/**
 * Where a folder link lands: its first entry this reader may open.
 *
 * A folder URL is what makes a section something you can send to somebody, which a scroll position
 * never was. Falling back to the landing path rather than 404-ing keeps a stale link harmless.
 */
export function settingsSectionLandingPath(
  actor: Actor | null,
  id: SettingsSectionId,
  kind: OrganizationKind = 'festival',
): SettingsEntryPath {
  const section = settingsSectionsFor(actor, kind).find((candidate) => candidate.id === id);
  return section?.entries.at(0)?.to ?? settingsLandingPath(actor);
}
